/**
 * Useful functions for generated classes.  Mostly for the server, but some are also used for validation methods on both
 * the client and server.
 *
 * @module lib/util
 */
import React, {ReactElement} from 'react';
import debounce from 'debounce';
import {captureException} from "./picos/error";
import upFirstLetter from "./picos/upFirstLetter";
import ts from "./picos/timestamp";
import * as uuids from "./picos/uuids";
import type {uuid} from "@/@types/schema";
import type {Comparable, EntityType} from "@/@types/entity";
import ArrayMap from "./ArrayMap";
import {format} from "date-fns";
import type {Range} from "../ui/DateComponents/DateRangePicker/DateRangePicker";
import Skeleton from "../ui/Skeleton";
import * as arrays from './picos/arrays';
import {socket} from './picos/socket';
import {toTwoDecimals as wasmToTwoDecimals} from './payments';

/**
 * Create a new high-resolution timestamp
 * @returns {number}
 */
export function newHRTimestamp(): number {
    return ts();
}

function billingIncrementToFraction(increment: number): ReactElement {
    if (increment < 1 || increment > 60 || !Number.isInteger(increment)) {
        return <span>{(increment/60).toFixed(2)} minutes</span>;
    }

    // Simplify the fraction increment/60
    const gcd = (a: number, b: number): number => b === 0 ? a : gcd(b, a % b);
    const divisor = gcd(increment, 60);
    const numerator = increment / divisor;
    const denominator = 60 / divisor;

    return <span><sup>{numerator}</sup>&frasl;<sub>{denominator}</sub><sup>th</sup> of an hour</span>;
}

/**
 * Debounce the given function for timeout milliseconds.
 *
 * @param {Function} f
 * @param {number} [timeout] - optional timeout, defaults to 100ms
 * @param {boolean} [immediate] - run immediately, or wait?
 */
function bouncify<T extends (...args: any[]) => any>(f: T, timeout: number = 100, immediate: boolean = true): T {
    return debounce(f, timeout, {immediate}) as unknown as T;
}

/**
 * Capitalize just the first letter of a string
 *
 * @param {string} name
 * @returns {string}
 */
function capitalize(name: string): string {
    if (name == null) return name;
    else if (name.length === 1) return name.toUpperCase();
    return name.charAt(0).toUpperCase() + name.slice(1);
}

/**
 * Generate a random UUIDv4 value.
 *
 * @returns {uuid} A random UUIDv4 value
 */
function genUUID(): uuid {
    return uuids.genUUID();
}

/**
 * Generate a sequence of integers from start to stop.
 *
 * @param {number} start
 * @param {number} stop
 * @returns {number[]}
 */
function genIntArray(start: number, stop: number): number[] {
    const result = [];
    for (let i = start; i <= stop; i++) result.push(i);
    return result;
}

/**
 * Based on the size of the screen, orientation, and ratio of height/width, figure out which layout style to use.
 *
 * For landscape view, we want to support left-side buttons on any screen under 1024 in width.
 *
 * @return {MenuLocation}
 */
function getMenuStyle(): string {
    if (window.innerWidth >= 800) return 'top';
    else return 'bottom';
}

/**
 * Get the number of units for a given amount.
 *
 * @param {string} unit
 * @param {number} amount
 * @returns {string}
 */
function getUnits(unit: string, amount: number): string {
    if (amount > 1) return unit + 's';
    else return unit;
}

/**
 * Get the visibility event name for this platform.
 *
 * @returns {{hidden: string, visibilityChange: string}}
 */
function getVisibilityInfo(): { hidden: string | null; visibilityChange: string | null; } {
    // Set the name of the hidden property and the change event for visibility
    let hidden: string;
    let visibilityChange: string;

    if (typeof document.hidden !== "undefined") { // Opera 12.10 and Firefox 18 and later support
        hidden = "hidden";
        visibilityChange = "visibilitychange";
    }
    // @ts-expect-error - doesn't know about IE
    else if (typeof document.msHidden !== "undefined") {
        hidden = "msHidden";
        visibilityChange = "msvisibilitychange";
    }
    // @ts-expect-error - doesn't know about webkit
    else if (typeof document.webkitHidden !== "undefined") {
        hidden = "webkitHidden";
        visibilityChange = "webkitvisibilitychange";
    }
    else {
        hidden = '';
        visibilityChange = '';
    }

    return {hidden, visibilityChange};
}

function reorderByPosition<T extends { position?: number | null; addedOn: number; clone(): T }>(
    objects: T[]
): T[] {
    // Sort objects: nulls last, then by position, then by addedOn for duplicates
    const sorted = [...objects].sort((a, b) => {
        if (a.position === null && b.position === null) return a.addedOn - b.addedOn;
        if (a.position === null) return 1;
        if (b.position === null) return -1;
        if (a.position === b.position) return a.addedOn - b.addedOn;
        return a.position - b.position;
    });

    const mutated: T[] = [];
    let expectedPosition = 1;

    for (const obj of sorted) {
        if (obj.position !== expectedPosition) {
            const cloned = obj.clone();
            cloned.position = expectedPosition;
            mutated.push(cloned);
        }
        expectedPosition++;
    }

    return mutated;
}

/**
 * Convert from snake to camel case.
 *
 * @param {string} s - the string to convert
 * @returns {string}
 */
function toCamel(s: string): string {
    return s.replace(/([-_][a-z])/ig, ($1) => {
        return $1.toUpperCase()
            .replace('-', '')
            .replace('_', '');
    });
}

/**
 * Convert from camel to snake case.
 * @param {string} s
 * @returns {string}
 */
function snakeToText(s: string): string {
    if (!s) {
        console.trace('sent a null to snakeToText');
        return '';
    }

    const sep = s.replace(/([-_][a-z])/ig, ($1) => {
        return $1.replace('-', ' ')
            .replace('_', ' ');
    });

    return sep.toLowerCase().split(' ').map(s => upFirstLetter(s)).join(' ');
}

/**
 * Round to {decimal} decimals.
 *
 * @param {number} number
 * @param {number} decimals
 * @returns {number}
 */
function toDecimals(number: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    return Math.round(number * factor) / factor;
}

/**
 * Convert number to two decimals, dropping any zeros at the end.
 *
 * Delegates to the WASM-backed `lib/payments` implementation, which
 * rounds half-up exactly (`1.005 → 1.01`, not `1.00`). The legacy
 * inline `Math.round(number * 100) / 100` mis-rounded at scale-100
 * float-drift boundaries — see OWA-451.
 *
 * **For DISPLAY only.** Intermediate calculations should stay at
 * scale 4 (use `addMoney` / `subtractMoney` / `multiplyMoneyBy` from
 * `lib/payments`); only round at the final UI boundary.
 *
 * @param {number} number
 * @returns {number}
 */
function toTwoDecimals(number: number): number {
    return wasmToTwoDecimals(number);
}

/**
 * Stringify a map
 *
 * @param {Map} map
 * @param {boolean} formatted
 * @returns {string}
 */
function mapToString(map: Map<string, any>, formatted: boolean = false): string {
    if (formatted) return JSON.stringify(Array.from(map.entries()), null, 2);
    else return JSON.stringify(Array.from(map.entries()));
}

/**
 * Parse a string into a map of key/values
 *
 * @param str
 * @returns {Map<string, any>}
 */
function stringToMap(str: string): Map<string, any> {
    return new Map(JSON.parse(str));
}

/**
 * Take a camel case word and return multiple words, with spaces, first letter uppercase.
 *
 * @param {string} str
 * @returns {string}
 */
function camelCaseToWords(str: string): string {
    return upFirstLetter(str.replace(/([A-Z]+)/g, " $1").replace(/([A-Z][a-z])/g, " $1"));
}

/**
 * Do a deep comparison of two objects and return true if they are equal.
 *
 * We ignore transient fields!
 *
 * Arrays are sorted by a compare function on the object, if available, otherwise default sorting is provided.
 *
 * @param {Object} obj1
 * @param {Object} obj2
 * @param {number} level - used for recursive processing, never call directly with this
 * @returns {boolean} - true if the objects are equal, false otherwise
 */
function deepCompare(obj1: string | any | Record<string, any> & Comparable<EntityType>, obj2: string | any | Record<string, any> & Comparable<EntityType>, level: number = 0): boolean {
    level++;

    try {
        if (typeof obj1 !== typeof obj2) throw new Error(`L${level} types do not match: ${typeof obj1}, ${typeof obj2}`);
        if (typeof obj1 === 'string' && typeof obj2 === 'string' && obj1 !== obj2) return false;
        const isArray1 = Array.isArray(obj1);
        const isArray2 = Array.isArray(obj2);
        if (isArray1 !== isArray2) throw new Error(`L${level} at least one is not an array. obj1: ${Array.isArray(obj1)} obj2: ${Array.isArray(obj2)}`);

        // If one or both is null, then either both are null (true) or one is not null (false)
        if (!obj1 || !obj2) {
            if (obj1 === obj2) return true;
            else {
                throw new Error(`L${level} one, but not both are null: ${obj1}, ${obj2}`);
            }
        }

        // Handle top-level recursion
        if (Array.isArray(obj1)) {
            // Quick check: if obj2 is an array and has a different length, they are not equal
            if (Array.isArray(obj2) && (obj1.length !== obj2.length)) return false;

            // Normal logic
            let s1: any[];
            let s2: any[];

            // Always sort arrays, use compare operator if available
            if ('compare' in obj1) s1 = obj1.sort((obj1 as Comparable<EntityType>).compare);
            else s1 = obj1.sort();

            if ('compare' in obj2) s2 = obj1.sort((obj2 as Comparable<EntityType>).compare);
            else s2 = obj2.sort();

            // Do an iterative comparison
            for (let i = 0; i < s1.length; i++) {
                switch (typeof (s1[i])) {
                    case 'object':
                        if (!deepCompare(s1[i], s2[i], level)) {
                            throw new Error(`L${level} deepCompare at index ${i} ${s1[i].constructor.name} <> ${s2[i].constructor.name}`);
                        }
                        break;
                    default:
                        if (s1[i] !== s2[i]) {
                            throw new Error(`L${level} simple compare failed at index ${i}: ${s1[i]} <> ${s2[i]}`);
                        }
                        break;
                }
            }
        }
        else {
            // Not an array, is it at least an object?
            if (typeof (obj1) !== 'object') {
                // Just do a simple comparison
                if (obj1 !== obj2) {
                    throw new Error(`L${level} simple type inequality between objects: ${obj1} <> ${obj2}`);
                }
            }
            else {
                // This is an object

                // Check for identical empty objects
                if ((Object.keys(obj1).length === 0) && (Object.keys(obj2).length === 0)) return true;
                else if (Object.keys(obj1).length !== Object.keys(obj2).length) return false;

                // Loop through properties in object 1
                Object.keys(obj1).forEach((p: string) => {
                    // Ignore transient properties
                    if (!(obj1.isTransientProperty && obj1.isTransientProperty(p))) {
                        // TODO: Testing
                        if (!(p === 'dbAddedOn' || p === 'createdOn')) {

                            // Check property exists on both objects
                            if (Object.prototype.hasOwnProperty.call(obj1, p) !== Object.prototype.hasOwnProperty.call(obj2, p)) {
                                throw new Error(`L${level} obj1 has a property that obj2 does not: ${p}: obj1 = ${Object.prototype.hasOwnProperty.call(obj1, p)} obj2 = ${Object.prototype.hasOwnProperty.call(obj2, p)}`);
                            }

                            // Now we need to examine this sub-field, obj1[p], and its counterpart, obj2[p]
                            let p1 = obj1[p];
                            let p2 = obj2[p];

                            if (p1 instanceof Date) {
                                if (p1 !== p2) throw new Error(`L${level} date inequality in property ${p}: ${p1} <> ${p2}`);
                            }

                            switch (typeof (p1)) {
                                //Deep compare objects.  Array is a type of object, which we will need to recurse
                                case 'object':
                                    if (!deepCompare(p1, p2, level)) {
                                        throw new Error(`L${level} deepCompare of ${p} failed: ${p1.constructor.name} <> ${p2.constructor.name}`);
                                    }
                                    break;
                                case 'function':
                                    //Compare function code
                                    if (typeof (p2) === 'undefined' || (p !== 'compare' && p1.toString() !== p2.toString())) {
                                        throw new Error(`L${level} unequal function definitions for ${p}`);
                                    }
                                    break;
                                default:
                                    // Regular string/numeric, just compare
                                    if (p1 !== p2) {
                                        throw new Error(`L${level} non-object inequality in property ${p}: ${p1} <> ${p2}`);
                                    }
                                    break;
                            }
                        }
                    }
                });

                //Check object 2 for any extra properties
                Object.keys(obj2).forEach((p: string) => {
                    if (obj2.isTransientProperty &&
                        !obj2.isTransientProperty(p) &&
                        typeof (obj1[p]) === 'undefined'
                    ) {
                        throw new Error(`L${level} obj2 has an extra non-transient property ${p}`);
                    }
                });
            }
        }
    }
    catch (err: any) {
        return false;
    }

    return true;
}

function defaultBoolean(vals: any[]): boolean {
    // Loop through vals until a defined value shows up and assign it
    for (let val of vals) {
        if (typeof (val) === "boolean") return val;
    }

    return false;
}

/**
 * Highlight a term in a string.
 *
 * @param {string} term
 * @param {string} str
 * @returns {string}
 */
function highlight(term: string, str: string): string {
    // If there is a '?' in the term, we can't process it
    if (term === '?') return str;

    try {
        const pattern = escapeRegEx('\\b' + term + '\\b');
        // eslint-disable-next-line security/detect-non-literal-regexp -- pattern wrapped via escapeRegEx, safe from injection
        const re = new RegExp(pattern, 'g');
        return str.replaceAll(re, `<mark>${term}</mark>`);
    }
    catch (err) {
        captureException(err as Error);
        return str;
    }
}

/**
 * Convert an hrt to a date.  Note that this is a lossy function, and you can not convert back
 * without losing a TON of precision.  So never convert a regular date to an HRT.
 *
 * @param {number} hrt - A high-res timestamp
 * @returns {Date}
 */
function hrtToDate(hrt: number): Date {
    return new Date(Math.round(hrt / 1000));
}

/**
 * Return an hrt in seconds.  Generally used to count seconds.
 * @param {number} hrt
 * @returns {number}
 */
function hrtToSeconds(hrt: number): number {
    return hrt / 1000000;
}

/**
 * Dummy function
 */
function noop() {
    // Do nothing
}

/**
 * Convert a YYYYMMDD string into an HRT (used for asOf calculations)
 *
 * @param {string} dateString  A string with YYYYMMDD format
 * @returns {number} A high-resolution timestamp for the given dateString
 */
function dateToHRT(dateString: any): number {
    if (typeof (dateString) !== 'string') throw new Error(`${dateString} is not a string`);
    if (dateString.length !== 8) throw new Error(`${dateString} should be YYYYMMDD`);

    const year = Number.parseInt(dateString.slice(0, 4));
    const monthIndex = Number.parseInt(dateString.slice(3, 2)) - 1;
    const day = Number.parseInt(dateString.slice(5, 2));
    let d = new Date(year, monthIndex, day);
    return d.getTime() * 1000;
}

/**
 * Create a parse function
 *
 * @param {string|number|null} [v]
 * @returns {number|null}
 */
function parseInt(v: string | number | null): number | null {
    if (v === null || v === undefined) return null;

    // If it's a number, send it back
    if (typeof v === 'number') {
        if (Number.isInteger(v)) return v;
        else {
            console.warn(`Rounding ${v} to make an integer`);
            return Math.round(v);
        }
    }

    // Not an integer, parse it.
    const r = Number.parseInt(v);

    // If NaN throw, otherwise return the number
    if (isNaN(r)) throw new Error(`tried to parse a non-numeric entity: ${r}`);
    else return r;
}

/**
 * Parse one or an array of ints
 *
 * @param {number|number[]|string|string[]|null} val - Either a single value or an array of values to parse to int
 * @returns {number|number[]|null}
 */
function parseInts(val: number | number[] | string | string[] | null): number | number[] | null {
    // We will go through the most likely use cases first

    // If null or undefined, just return null
    if (!val) return val === 0 ? 0 : null

    // If this is just a number, return it
    if (Number.isInteger(val) && typeof (val) === 'number') return val;

    // If this is a string, parse it.
    if (typeof (val) === 'string') return this.parseInt(val);

    // If this is an array of all integers, just return it
    if (Array.isArray(val) && val.every(v => Number.isInteger(v) && typeof (v) === 'number')) return val as number[];

    // There are some strings involved
    if (Array.isArray(val)) return val.map((v: string | number) => typeof (v) === 'string' ? this.parseInt(v) : v) as number[];
    else this.parseInt(val);
}

/**
 * Create a parse function for floats
 * @param {string|number|null} [v]
 * @returns {number|null}
 */
function parseFloat(v: string | number | null): number | null {
    if (v === 0) return 0;

    if (!v) return null;

    if (typeof (v) === 'number') return v;

    // Not a number, parse it.
    const r = Number.parseFloat(v);

    // If NaN throw, otherwise return the number
    if (isNaN(r)) throw new Error(`tried to parse a non-numeric entity: ${r}`);
    else return r;
}

/**
 * Parse one or an array of floats in number or string form
 *
 * @param {number|number[]|string|string[]|null} [val] - Either a single value or an array of values
 * @returns {number|number[]|null}
 */
function parseFloats(val: number | number[] | string | string[] | null): number | number[] | null {
    // We will go through the most likely use cases first

    // If null or undefined, just return null
    if (!val) return null;

    // If this is just a number, return it
    if (typeof (val) === 'number') return val;

    // If this is a string, parse it.
    if (typeof (val) === 'string') return this.parseFloat(val);

    // If this is an array of all numbers, just return it
    if (Array.isArray(val) && val.every(v => typeof (v) === 'number')) return val as number[];

    // There are some strings involved
    if (Array.isArray(val)) return val.map(v => this.parseFloat(v)) as number[];
    else return this.parseFloat(val);
}

/**
 * Parse a search string into a mapping.
 *
 * @param {string} qs - queryString, starting with ?, e.g. ?id=5&type=cow
 * @returns {object}
 */
function parseQueryString(qs: string): Record<string, string> {
    const result = {};
    const usp = new URLSearchParams(qs);

    for (const p of usp) {
        result[p[0]] = p[1];
    }

    return result;
}

/**
 * @param {string|null} [s]
 * @returns {string|null}
 */
function trimRight(s: string | null): string | null {
    if (!s) return null;
    if (s[s.length - 1] === ' ') return s.trimEnd();
    else return s;
}

/**
 * Trim right trailing spaces for a string or array of strings
 *
 * @param {string|string[]|null} str - Either a single value or an array of values to trim
 * @returns {string|string[]|null}
 */
function trimRights(str: string | string[] | null): string | string[] | null {
    if (!str) return str;

    if (Array.isArray(str)) {
        return str.map(s => {
            return trimRight(s) as string;
        });
    }
    else return trimRight(str);
}

/**
 * Strip functions out of an object.
 *
 * @param {object} obj
 * @return {object}
 */
function stripObject(obj: object): object {
    return JSON.parse(JSON.stringify(obj));
}

/**
 *
 * @param {string} str
 * @returns {string | null}
 */
function stripQuotes(str: string): string {
    if (!str) return '';
    if (str.length > 2) {
        const startsWith = ['"', "'"].includes(str[0]);
        const endsWith = ['"', "'"].includes(str[str.length - 1]);
        if (startsWith && endsWith) return str.substring(1, str.length - 1);
        else return str;
    }
    else return str;
}

/**
 * Validate a function by trying to run it.
 *
 * @param {Function} fn
 */
function validate(fn: Function) {
    try {
        fn();

        return true;
    }
    catch (e: any) {
        return false;
    }
}

/**
 * Take an array of filter functions and create a compound filter
 * @param {function[]} filters
 */
function combineFilters(filters: Function[]) {
    return (t: any) => {
        let bail = false;
        for (let i = 0; i < filters.length; i++) {
            if (!filters[i]?.(t)) {
                bail = true;
                break;
            }
        }

        return !bail;
    }
}

/**
 * Easy filter to grab unique values from an array of primitives.
 *
 * @param {any} value
 * @param {number} index
 * @param {any[]} self
 * @returns {boolean}
 */
function distinctFilter(value: any, index: number, self: any[]): boolean {
    return self.indexOf(value) === index;
}

/**
 * Return an array of objects with distinct id fields, using the supplied field name
 * @param {string} field
 * @param {any[]} sourceArray
 * @returns {any[]}
 */
function distinctIdByField(field: string, sourceArray: any[]): any[] {
    return distinctId(sourceArray, field);
}

/**
 * Return an array of object with distinct id fields, using an optional
 * id field name. Defaults to 'id'.
 *
 * @param {any[]} sourceArray
 * @param {string} [field]
 * @returns {any[]}
 */
function distinctId<T = any>(sourceArray: T[], field: string = 'id'): T[] {
    const length = sourceArray.length;
    const result = []
    const seen = new Set();

    for (let index = 0; index < length; index++) {
        let value = sourceArray[index];

        if (!seen.has(value[field])) {
            seen.add(value[field]);
            result.push(value);
        }
    }

    return result;
}

/**
 * Easy filter to ensure only one of each entity (by entity.id) is present.
 *
 * @param {any} value
 * @param {number} index
 * @param {any[]} self
 * @returns {boolean}
 */
function distinctIdFilter(value: any, index: number, self: any[]): boolean {
    return self.slice(0, index + 1).filter(c => c.id === value.id).length === 1;
}

/**
 * Get rid of entities with the deleted flag set.
 *
 * @param {EntityType} value
 * @returns {boolean}
 */
function deletedFilter(value: EntityType): boolean {
    return value.status !== 'D';
}

/**
 * Easy filter to grab unique values from an array of primitives.
 *
 * @param {<T>} value
 * @returns {boolean}
 */
// OWA-554: a type guard (not just `: boolean`) so `arr.filter(existsFilter)`
// narrows the element type. The predicate matches the runtime exactly — it keeps
// values that are non-null, non-undefined AND non-false — so the precise guard is
// `Exclude<NonNullable<T>, false>`. This also strips the `false` arm of the common
// `cond && obj` pattern, turning `(false | Obj)[]` into `Obj[]`.
function existsFilter<T>(value: T): value is Exclude<NonNullable<T>, false> {
    return value !== null && value !== undefined && value !== false;
}

/**
 * Combination of exists and not delete filters
 * @param {EntityType} value
 * @returns {boolean}
 */
function existsAndNotDeletedFilter<T extends EntityType>(value: T | null | undefined): value is T {
    return !!value && value.status !== 'D';
}

/**
 * Take several values, of type numeric, and return the first numeric value
 * @param {number} a
 * @param {number} b
 * @param {number} c
 * @param {number} d
 * @returns {number}
 */
function coalesce(a: number, b: number, c: number, d: number): number {
    return a ?? b ?? c ?? d;
}

/**
 * Take an array of values with duplicates, and give back a map of values to counts.
 *
 * @param {string[]} values
 * @returns {{value: string, count: number}[]}
 */
function countDistinct(values: string[]): { value: string, count: number }[] {
    const distinctValues = values.filter(distinctFilter);
    return distinctValues.map((value: string) => ({value, count: values.filter((v: string) => v === value).length}));
}

/**
 * T is the class we want to ensure.
 *
 * @param {typeof EntityType} baseType
 * @param {object} item
 * @returns {EntityType}
 */
function ensure<T>(baseType: any, item: object): T {
    if (!item) return null;
    if (item instanceof baseType) return item as T;
    else return new baseType({...item});
}

/**
 * Get the navigation page. Used by EditableTable users to get a unique name for the table.
 * @returns {string}
 */
function getPageRoot(): string {
    return window.location.pathname;
}

/**
 *
 * @param {number} value
 * @param {number} digits - number of digits after .
 * @returns {number}
 */
function fixed(value: number, digits: number): number {
    if (!value) return value;

    const remainder = Math.floor(value) - value;
    if (!remainder) return value;
    else return Number.parseFloat(value.toFixed(digits));
}

/**
 * Flatten an array. MUCH faster than Array.flat()!!!
 *
 * @param {array} arr - the array to flatten
 * @param [result] - for internal recursion use only!
 * @returns {Array}
 */
function flatten(arr: Array<any>, result: any[] = []): any[] {
    for (let i = 0, length = arr.length; i < length; i++) {
        const value = arr[i];
        if (Array.isArray(value)) {
            flatten(value, result);
        }
        else {
            result.push(value);
        }
    }
    return result;
}

/**
 * Check if we can get a connection to the server.
 *
 * @returns {boolean}
 */
function isConnected(): boolean {
    return (navigator.onLine && socket?.connected);
}

/**
 * Basic alphanumeric sort.
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
function alphaSort(a: string, b: string): number {
    if (a < b) {
        return -1;
    }
    if (a > b) {
        return 1;
    }
    return 0;
}

/**
 * Promisify setState
 * @param {object} _this
 * @param {Function} _this.setState
 * @param {object} state
 * @return {Promise}
 */
function setStateAsync(_this: { setState: Function }, state: object): Promise<any> {
    return new Promise(resolve => _this.setState(state, resolve));
}

/**
 *
 * @param {Function} fnToCall
 * @param {any[]} args
 * @return {Promise}
 */
function promisifyCallback(fnToCall: Function, ...args: any[]): Promise<any> {
    return new Promise(resolve => fnToCall(...args, resolve));
}

/**
 *
 * @param {array} entityArray
 * @param {string} fieldName
 * @returns {array}
 */
function sortEntityArrayByString<T extends EntityType>(entityArray: ArrayMap<T> | Array<T>, fieldName: string): Array<T> {
    return entityArray.filter(existsFilter)
        .sort((a, b) => {
            let A = a[fieldName];
            let B = b[fieldName];
            if (A && B) return A.localeCompare(B);
            else if (A && !B) return 1;
            else return -1;
        });
}

/**
 *
 * @param {ArrayMap|array} entityArray
 * @param {function} fn - a function that takes an entity and returns a string, which is used for string compare
 * @returns {*}
 */
function sortEntityArrayByStringFunction<T extends EntityType>(entityArray: ArrayMap<T> | T[], fn: (item: T) => string): T[] {
    return entityArray.filter(existsFilter)
        .sort((a: T, b: T) => {
            let A = fn(a);
            let B = fn(b);
            if (A && B) return A.localeCompare(B);
            else if (A && !B) return 1;
            else return -1;
        });
}

/**
 * Sorts DESCENDING!
 *
 * @param {ArrayMap|array} entityArray
 * @param {string} fieldName
 * @param {string} [direction]
 * @param {function} [secondaryFn] - if dates match, optional secondary function to sort by (must be sort function taking a, b as objects)
 * @returns {*}
 */
function sortEntityArrayByDate<T extends EntityType>(
    entityArray: ArrayMap<T> | Array<T>,
    fieldName: string,
    direction: string = 'asc',
    secondaryFn?: Function
): T[] {
    try {
        return entityArray.sort((a: T, b: T): number => {
            let A = a[fieldName];
            let B = b[fieldName];

            if (direction === 'desc') {
                if (A && B) {
                    if ((A === B) && secondaryFn) return -1 * secondaryFn(a, b);
                    else {
                        if (A instanceof Date && B instanceof Date)
                            return B.getDate() - A.getDate();
                        else if (typeof A === 'number' && typeof B === 'number')
                            return B - A;
                        else throw new Error(`${fieldName} is neither a number nor a date, can not sort`);
                    }
                }
                else if (A && !B) return 1;
                else return -1;
            }
            else {
                if (A && B) {
                    if ((A === B) && secondaryFn) return secondaryFn(a, b);
                    if (A instanceof Date && B instanceof Date)
                        return A.getDate() - B.getDate();
                    else if (typeof A === 'number' && typeof B === 'number')
                        return A - B;
                    else throw new Error(`${fieldName} is neither a number nor a date, can not sort`);
                }
                else if (B && !A) return 1;
                else return -1;
            }
        });
    }
    catch (e: any) {
        console.trace(e.message);
    }
}

/**
 * Sort an array of objects by a position field.
 *
 * @param {any} a
 * @param {number} a.position
 * @param {any} b
 * @param {number} b.position
 */
function sortByPosition(a: any, b: any): number {
    if (a.position === b.position) return a.addedOn - b.addedOn;
    else return (a.position || 0) - (b.position || 0);
}



/**
 * Show a skeleton for no data.
 *
 * @param {string} [message] - default message to display in the skeleton
 * @param {Range} [dateRange] - date range to display in the skeleton
 * @returns {Skeleton}
 */
function noData(message?: string, dateRange?: Range): ReactElement {
    if (!message && !dateRange) message = 'No data for this time period';
    if (dateRange) message = `No data for ${format(dateRange.startDate, 'yyyy-MM-dd')} to ${format(dateRange.endDate, 'yyyy-MM-dd')}`;

    return <div style={{margin: "auto"}}><Skeleton message={message} noMargin={false}/></div>;
}

/**
 * Takes a number and returns a currency-formatted string with commas.
 *
 * @param {number|string} num - original number to be formatted
 * @param {number} decimals - how many digits to the right of the decimal point?
 * @returns {string}
 */
function numberWithCommas(num: number | string, decimals: number = 2): string {
    if (!num) return Number.parseFloat('0.00').toFixed(decimals);
    if (typeof (num) !== 'number') num = Number.parseFloat(num);

    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(num);
}

/**
 * Takes a number and returns a currency-formatted string with commas and dollar sign in front.
 *
 * @param {number} num
 * @param {boolean} [textOnly]
 * @param {boolean} [noCents]
 * @param {number} [fixedLength] - all numbers returned fixed length, padded with spaces in front
 * @returns {ReactElement|string}
 */
function formatCurrency(num: number, textOnly?: boolean, noCents?: boolean, fixedLength?: number): ReactElement | string {
    const fmtCurrency = numberWithCommas(num);
    const [signedDollars, cents] = fmtCurrency.split('.');
    const isNeg = signedDollars.startsWith('-');
    const dollars = isNeg ? signedDollars.slice(1) : signedDollars;
    const sign = isNeg ? '-' : '';

    const padNumber = (d: string) => {
        if (fixedLength && (fixedLength > 0)) return `${d}`.padStart(fixedLength, ' ');
        else return d;
    };

    if (textOnly) {
        if (noCents) return padNumber(`${sign}$${dollars}`);
        else return padNumber(`${sign}$${dollars}.${cents}`);
    }
    else {
        const negative = isNeg ? 'u-currency-negative' : '';

        return (
            <span className={`u-currency ${negative}`.trim()}>
            {isNeg && <span className='u-currency-sign'>-</span>}
            <span className={`u-currency-symbol ${noCents ? 'u-currency-symbol-no-cents' : ''}`}>$</span>
            <span className='u-currency-dollars'>{dollars}
                {!noCents && <span className='u-currency-dot'>.</span>}
                {!noCents && <sup className='u-currency-cents'>{cents}</sup>}
            </span>
        </span>
        );
    }
}

function formatCurrencyAsString(num: number, noCents?: boolean, fixedLength?: number): string {
    const fmtCurrency = numberWithCommas(num);
    const [signedDollars, cents] = fmtCurrency.split('.');
    const isNeg = signedDollars.startsWith('-');
    const dollars = isNeg ? signedDollars.slice(1) : signedDollars;
    const sign = isNeg ? '-' : '';

    const padNumber = (d: string) => {
        if (fixedLength && (fixedLength > 0)) return `${d}`.padStart(fixedLength, ' ');
        else return d;
    };

    if (noCents) return padNumber(`${sign}$${dollars}`);
    else return padNumber(`${sign}$${dollars}.${cents}`);
}

/**
 * Utility function just for matchWholeWord
 * @param {string} str
 * @returns {string}
 */
export function escapeRegEx(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Find a whole term in a search string
 * @param {string} searchTerm - the term to search for
 * @param {string} searchString - the string to search in
 * @returns {boolean} true if found, false if not found
 */
function matchWholeWord(searchTerm: string, searchString: string): boolean {
    let regex = '\\b';
    regex += searchTerm;
    regex += '\\b';

    // eslint-disable-next-line security/detect-non-literal-regexp -- pattern wrapped via escapeRegEx, safe from injection
    return new RegExp(escapeRegEx(regex), "").test(searchString);
}

/**
 * Get the boolean value of the truthy value
 * @param {any} value
 * @param {boolean} useTorF - return a 't' or 'f' as the result, rather than boolean true/false
 *
 * @return {string|boolean|*}
 */
function getBoolean(value: any, useTorF: boolean): string | boolean | any {
    if (value == null) return value;
    else if (value === 'f' || value === 'false' || value === false) {
        if (useTorF) return 'f';
        else return false;
    }
    else if (useTorF) return 't';
    else return true;
}

/**
 * Friendly, user-facing error message for an EntityType. Suitable for inline
 * display next to the entity (e.g. the maroon error in the Case/Lead column on
 * the Invoices grid). Strips the `caused by Error:` prefix that
 * restify-errors / WError emits when a server response wraps a bare Error
 * object — that prefix is a serialization artifact, not user content. For the
 * unmodified message (debug / tooltip), use `getRawErrorMessage`.
 */
function getErrorMessage(item: EntityType): string {
    const raw = item.fatalError?.message ?? '';
    if (!raw) return '';
    const stripped = raw.replace(/^caused by (?:\w+Error|Error):\s*/, '');
    if (stripped === 'Record not found') return 'A linked record is missing — open and re-save';
    return stripped;
}

/**
 * Raw fatalError.message for an EntityType — pass to a `title=` attribute or
 * tooltip so the underlying technical message stays reachable for debugging.
 */
function getRawErrorMessage(item: EntityType): string {
    return item.fatalError?.message ?? '';
}

/**
 * Only called if there is one argument to the memd function, and no deps.
 *
 * @param {{id: string, addedOn: number}} obj
 * @returns {*|string|null}
 */
function cacheKey(obj: { id: string, addedOn: number }): any | string | null {
    if (obj == null) return null;
    else if (Array.isArray(obj) && obj[0]?.hasOwnProperty('addedOn')) {
        return obj.map(o => `${o.id} ${o.addedOn}`).join(' ');
    }
    else if (Object.prototype.hasOwnProperty.call(obj, 'addedOn')) {
        return `${obj.id} ${obj.addedOn}`;
    }
    else return JSON.stringify(obj);
}

/**
 *
 * @param {string} entityName - entity name
 * @param {object} objectToAssess - the object to assess delete impact on
 * @param {function} deleteImpactFn - the deleteImpact function to call
 * @return {{canDelete: boolean, message: JSX.Element}}
 */
function getDeleteImpact(entityName: string, objectToAssess: object, deleteImpactFn: Function): { canDelete: boolean; message?: ReactElement; } {
    try {
        const impacts = deleteImpactFn(objectToAssess);
        const isRequired = impacts.some((impact: { value: string }) => impact.value.endsWith('*'));
        const message = isRequired
                        ? 'and can not be deleted safely*' :
                        'but can be deleted safely';

        if (impacts.length) {
            return {
                canDelete: !isRequired, message: <div>This {entityName} is used in other places {message}.
                    <ul>
                        {impacts.map((impact: { value: string, count: number }, i: number) => <li key={i}>{impact.count} {impact.value}</li>)}
                    </ul>

                    {!isRequired &&
						<span>Are you sure you want to delete this {entityName}? It will delete all associated entries!</span>
                    }
                </div>
            };
        }
        else return {canDelete: true, message: null};
    }
    catch (err) {
        console.error(err);
        return {canDelete: false, message: <div>Error occurred calculating delete impact.</div>};
    }
}

/**
 * Given an entity array or arrays, return the most recent addedOn date.  Great for keys in React.
 *
 * @param {array} arrays
 * @returns {number}
 */
function getMaxAddedOn(arrays: Array<any>): number {
    return Math.max(...flatten(arrays).map(o => o.addedOn));
}

// Re-exports from arrays.ts
const arrayIntersection = arrays.arrayIntersection;
const arrayIncludesAllTerms = arrays.arrayIncludesAllTerms;
const arrayMatch = arrays.arrayMatch;
const arrayExclusions = arrays.arrayExclusions;
const arrayMerge = arrays.arrayMerge;

export default {
    alphaSort,
    arrayIntersection,
    arrayIncludesAllTerms,
    arrayMatch,
    arrayExclusions,
    arrayMerge,
    billingIncrementToFraction,
    bouncify,
    cacheKey,
    camelCaseToWords,
    capitalize,
    coalesce,
    countDistinct,
    dateToHRT,
    defaultBoolean,
    deepCompare,
    deletedFilter,
    combineFilters,
    distinctFilter,
    distinctId,
    distinctIdByField,
    distinctIdFilter,
    ensure,
    existsFilter,
    existsAndNotDeletedFilter,
    fixed,
    flatten,
    formatCurrency,
    formatCurrencyAsString,
    getErrorMessage,
    getRawErrorMessage,
    getBoolean,
    getPageRoot,
    genIntArray,
    getDeleteImpact,
    genUUID,
    getMaxAddedOn,
    getMenuStyle,
    getUnits,
    getVisibilityInfo,
    highlight,
    hrtToDate,
    hrtToSeconds,
    isConnected,
    mapToString,
    matchWholeWord,
    newHRTimestamp,
    noData,
    noop,
    numberWithCommas,
    parseInt,
    parseInts,
    parseFloat,
    parseFloats,
    parseQueryString,
    promisifyCallback,
    reorderByPosition,
    setStateAsync,
    snakeToText,
    sortEntityArrayByDate,
    sortEntityArrayByString,
    sortEntityArrayByStringFunction,
    sortByPosition,
    stringToMap,
    stripObject,
    stripQuotes,
    toCamel,
    toDecimals,
    toTwoDecimals,
    trimRight,
    trimRights,
    validate
};
